import React, { Component } from 'react';
import { 
	Modal, 
	Form, 
	Input, 
	Select, 
	Switch, 
	Upload, 
	Button, 
	Avatar, 
	Row, 
	Col, 
	Divider,
	message,
	Tabs,
	DatePicker,
	InputNumber,
	Checkbox
} from 'antd';
import {
	UserOutlined,
	UploadOutlined,
	EyeInvisibleOutlined,
	EyeTwoTone,
	MailOutlined,
	PhoneOutlined,
	GlobalOutlined,
	CalendarOutlined,
	CrownOutlined,
	SafetyOutlined,
	VideoCameraOutlined,
	SaveOutlined,
	CloseOutlined
} from '@ant-design/icons';
import moment from 'moment';
import './UserModal.scss';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Password } = Input;

class UserModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			activeTab: '1',
			avatarUrl: null,
			avatarFile: null,
			uploadLoading: false,
			passwordVisible: false,
			confirmPasswordVisible: false
		};
		this.formRef = React.createRef();
	}

	componentDidUpdate(prevProps) {
		if (this.props.visible && !prevProps.visible) {
			this.resetForm();
		}
	}

	// Reset form when modal opens
	resetForm = () => {
		const { user } = this.props;
		
		if (user) {
			// Edit mode - populate form with user data
			this.formRef.current?.setFieldsValue({
				username: user.username,
				email: user.email,
				displayName: user.displayName,
				role: user.role,
				status: user.status,
				verified: user.verified,
				firstName: user.firstName || '',
				lastName: user.lastName || '',
				phone: user.phone || '',
				website: user.website || '',
				bio: user.bio || '',
				birthDate: user.birthDate ? moment(user.birthDate) : null,
				country: user.country || '',
				city: user.city || '',
				timezone: user.timezone || '',
				language: user.language || 'en',
				emailNotifications: user.emailNotifications !== false,
				pushNotifications: user.pushNotifications !== false,
				marketingEmails: user.marketingEmails !== false,
				twoFactorAuth: user.twoFactorAuth || false,
				profilePrivate: user.profilePrivate || false,
				allowMessages: user.allowMessages !== false,
				showOnlineStatus: user.showOnlineStatus !== false
			});
			this.setState({ 
				avatarUrl: user.avatar,
				activeTab: '1'
			});
		} else {
			// Create mode - reset form
			this.formRef.current?.resetFields();
			this.setState({ 
				avatarUrl: null,
				avatarFile: null,
				activeTab: '1'
			});
		}
	};

	// Handle form submit
	handleSubmit = async () => {
		try {
			const values = await this.formRef.current?.validateFields();
			this.setState({ loading: true });

			// Format data
			const userData = {
				...values,
				birthDate: values.birthDate ? values.birthDate.toISOString() : null,
				avatar: this.state.avatarUrl
			};

			// If editing, include user ID
			if (this.props.user) {
				userData.id = this.props.user.id;
			}

			// TODO: Upload avatar if changed
			if (this.state.avatarFile) {
				const avatarUrl = await this.uploadAvatar(this.state.avatarFile);
				userData.avatar = avatarUrl;
			}

			// Call parent save handler
			await this.props.onSave(userData);
			
			this.setState({ loading: false });
		} catch (error) {
			console.error('Form validation failed:', error);
			this.setState({ loading: false });
		}
	};

	// Handle avatar upload
	handleAvatarChange = (info) => {
		if (info.file.status === 'uploading') {
			this.setState({ uploadLoading: true });
			return;
		}

		if (info.file.status === 'done') {
			// Get this url from response in real implementation
			this.getBase64(info.file.originFileObj, (url) => {
				this.setState({
					avatarUrl: url,
					avatarFile: info.file.originFileObj,
					uploadLoading: false
				});
			});
		}
	};

	// Convert file to base64
	getBase64 = (img, callback) => {
		const reader = new FileReader();
		reader.addEventListener('load', () => callback(reader.result));
		reader.readAsDataURL(img);
	};

	// Upload avatar to server
	uploadAvatar = async (file) => {
		// TODO: Implement actual avatar upload
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve(this.state.avatarUrl);
			}, 1000);
		});

		/* TODO: Replace with actual upload
		const formData = new FormData();
		formData.append('avatar', file);
		
		const response = await fetch('/api/upload/avatar', {
			method: 'POST',
			body: formData,
			headers: {
				'Authorization': `Bearer ${token}`
			}
		});
		
		const data = await response.json();
		return data.url;
		*/
	};

	// Validate upload
	beforeUpload = (file) => {
		const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
		if (!isJpgOrPng) {
			message.error('You can only upload JPG/PNG file!');
		}
		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isLt2M) {
			message.error('Image must smaller than 2MB!');
		}
		return isJpgOrPng && isLt2M;
	};

	// Handle tab change
	handleTabChange = (key) => {
		this.setState({ activeTab: key });
	};

	// Generate random password
	generatePassword = () => {
		const length = 12;
		const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
		let password = "";
		for (let i = 0; i < length; i++) {
			password += charset.charAt(Math.floor(Math.random() * charset.length));
		}
		this.formRef.current?.setFieldsValue({ password, confirmPassword: password });
		message.success('Password generated successfully');
	};

	render() {
		const { visible, user, onClose } = this.props;
		const { loading, activeTab, avatarUrl, uploadLoading } = this.state;
		const isEdit = !!user;

		const uploadButton = (
			<div className="avatar-upload-button">
				{uploadLoading ? <LoadingOutlined /> : <UploadOutlined />}
				<div style={{ marginTop: 8 }}>Upload</div>
			</div>
		);

		return (
			<Modal
				title={
					<div className="modal-title">
						<UserOutlined className="title-icon" />
						{isEdit ? `Edit User: ${user.username}` : 'Create New User'}
					</div>
				}
				open={visible}
				onCancel={onClose}
				width={800}
				className="user-modal"
				footer={[
					<Button key="cancel" onClick={onClose} icon={<CloseOutlined />}>
						Cancel
					</Button>,
					<Button 
						key="submit" 
						type="primary" 
						loading={loading}
						onClick={this.handleSubmit}
						icon={<SaveOutlined />}
					>
						{isEdit ? 'Update User' : 'Create User'}
					</Button>
				]}
				destroyOnClose
			>
				<Form
					ref={this.formRef}
					layout="vertical"
					className="user-form"
					initialValues={{
						status: 'active',
						role: 'user',
						verified: false,
						language: 'en',
						emailNotifications: true,
						pushNotifications: true,
						marketingEmails: false,
						twoFactorAuth: false,
						profilePrivate: false,
						allowMessages: true,
						showOnlineStatus: true
					}}
				>
					<Tabs activeKey={activeTab} onChange={this.handleTabChange} className="user-tabs">
						
						{/* Basic Information Tab */}
						<TabPane tab="Basic Info" key="1">
							<Row gutter={[24, 16]}>
								{/* Avatar Upload */}
								<Col span={24}>
									<div className="avatar-section">
										<div className="avatar-preview">
											<Avatar 
												size={80} 
												src={avatarUrl} 
												icon={<UserOutlined />}
												className="user-avatar-preview"
											/>
										</div>
										<div className="avatar-upload">
											<Upload
												name="avatar"
												listType="picture-card"
												className="avatar-uploader"
												showUploadList={false}
												beforeUpload={this.beforeUpload}
												onChange={this.handleAvatarChange}
												customRequest={({ onSuccess }) => {
													setTimeout(() => {
														onSuccess("ok");
													}, 0);
												}}
											>
												{avatarUrl ? null : uploadButton}
											</Upload>
											<div className="avatar-tips">
												<p>Click to upload avatar</p>
												<p>JPG/PNG, max 2MB</p>
											</div>
										</div>
									</div>
								</Col>

								{/* Username */}
								<Col xs={24} md={12}>
									<Form.Item
										name="username"
										label="Username"
										rules={[
											{ required: true, message: 'Please enter username' },
											{ min: 3, message: 'Username must be at least 3 characters' },
											{ pattern: /^[a-zA-Z0-9_]+$/, message: 'Username can only contain letters, numbers and underscore' }
										]}
									>
										<Input 
											prefix={<UserOutlined />}
											placeholder="Enter username"
											disabled={isEdit} // Username cannot be changed
										/>
									</Form.Item>
								</Col>

								{/* Email */}
								<Col xs={24} md={12}>
									<Form.Item
										name="email"
										label="Email"
										rules={[
											{ required: true, message: 'Please enter email' },
											{ type: 'email', message: 'Please enter valid email' }
										]}
									>
										<Input 
											prefix={<MailOutlined />}
											placeholder="Enter email address"
										/>
									</Form.Item>
								</Col>

								{/* Display Name */}
								<Col xs={24} md={12}>
									<Form.Item
										name="displayName"
										label="Display Name"
										rules={[
											{ required: true, message: 'Please enter display name' }
										]}
									>
										<Input placeholder="Enter display name" />
									</Form.Item>
								</Col>

								{/* Role */}
								<Col xs={24} md={12}>
									<Form.Item
										name="role"
										label="Role"
										rules={[
											{ required: true, message: 'Please select role' }
										]}
									>
										<Select placeholder="Select user role">
											<Option value="user">
												<UserOutlined /> User
											</Option>
											<Option value="streamer">
												<VideoCameraOutlined /> Streamer
											</Option>
											<Option value="moderator">
												<SafetyOutlined /> Moderator
											</Option>
											<Option value="admin">
												<CrownOutlined /> Admin
											</Option>
										</Select>
									</Form.Item>
								</Col>

								{/* Status */}
								<Col xs={24} md={12}>
									<Form.Item
										name="status"
										label="Status"
										rules={[
											{ required: true, message: 'Please select status' }
										]}
									>
										<Select placeholder="Select user status">
											<Option value="active">Active</Option>
											<Option value="inactive">Inactive</Option>
											<Option value="banned">Banned</Option>
											<Option value="pending">Pending</Option>
										</Select>
									</Form.Item>
								</Col>

								{/* Verified */}
								<Col xs={24} md={12}>
									<Form.Item
										name="verified"
										label="Verified User"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Verified" 
											unCheckedChildren="Unverified"
										/>
									</Form.Item>
								</Col>
							</Row>
						</TabPane>

						{/* Personal Information Tab */}
						<TabPane tab="Personal Info" key="2">
							<Row gutter={[24, 16]}>
								{/* First Name */}
								<Col xs={24} md={12}>
									<Form.Item
										name="firstName"
										label="First Name"
									>
										<Input placeholder="Enter first name" />
									</Form.Item>
								</Col>

								{/* Last Name */}
								<Col xs={24} md={12}>
									<Form.Item
										name="lastName"
										label="Last Name"
									>
										<Input placeholder="Enter last name" />
									</Form.Item>
								</Col>

								{/* Phone */}
								<Col xs={24} md={12}>
									<Form.Item
										name="phone"
										label="Phone Number"
									>
										<Input 
											prefix={<PhoneOutlined />}
											placeholder="Enter phone number"
										/>
									</Form.Item>
								</Col>

								{/* Website */}
								<Col xs={24} md={12}>
									<Form.Item
										name="website"
										label="Website"
									>
										<Input 
											prefix={<GlobalOutlined />}
											placeholder="https://example.com"
										/>
									</Form.Item>
								</Col>

								{/* Birth Date */}
								<Col xs={24} md={12}>
									<Form.Item
										name="birthDate"
										label="Birth Date"
									>
										<DatePicker 
											style={{ width: '100%' }}
											placeholder="Select birth date"
											disabledDate={(current) => current && current > moment().endOf('day')}
										/>
									</Form.Item>
								</Col>

								{/* Language */}
								<Col xs={24} md={12}>
									<Form.Item
										name="language"
										label="Language"
									>
										<Select placeholder="Select language">
											<Option value="en">English</Option>
											<Option value="zh">Chinese</Option>
											<Option value="ja">Japanese</Option>
											<Option value="ko">Korean</Option>
											<Option value="es">Spanish</Option>
											<Option value="fr">French</Option>
											<Option value="de">German</Option>
										</Select>
									</Form.Item>
								</Col>

								{/* Country */}
								<Col xs={24} md={12}>
									<Form.Item
										name="country"
										label="Country"
									>
										<Input placeholder="Enter country" />
									</Form.Item>
								</Col>

								{/* City */}
								<Col xs={24} md={12}>
									<Form.Item
										name="city"
										label="City"
									>
										<Input placeholder="Enter city" />
									</Form.Item>
								</Col>

								{/* Bio */}
								<Col span={24}>
									<Form.Item
										name="bio"
										label="Bio"
									>
										<TextArea 
											rows={4}
											placeholder="Enter user bio..."
											maxLength={500}
											showCount
										/>
									</Form.Item>
								</Col>
							</Row>
						</TabPane>

						{/* Security Tab */}
						<TabPane tab="Security" key="3">
							<Row gutter={[24, 16]}>
								{!isEdit && (
									<>
										{/* Password */}
										<Col xs={24} md={12}>
											<Form.Item
												name="password"
												label="Password"
												rules={[
													{ required: !isEdit, message: 'Please enter password' },
													{ min: 8, message: 'Password must be at least 8 characters' }
												]}
											>
												<Password 
													placeholder="Enter password"
													iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
												/>
											</Form.Item>
										</Col>

										{/* Confirm Password */}
										<Col xs={24} md={12}>
											<Form.Item
												name="confirmPassword"
												label="Confirm Password"
												dependencies={['password']}
												rules={[
													{ required: !isEdit, message: 'Please confirm password' },
													({ getFieldValue }) => ({
														validator(_, value) {
															if (!value || getFieldValue('password') === value) {
																return Promise.resolve();
															}
															return Promise.reject(new Error('Passwords do not match'));
														},
													}),
												]}
											>
												<Password 
													placeholder="Confirm password"
													iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
												/>
											</Form.Item>
										</Col>

										{/* Generate Password Button */}
										<Col span={24}>
											<Button 
												type="dashed" 
												onClick={this.generatePassword}
												className="generate-password-btn"
											>
												Generate Random Password
											</Button>
										</Col>

										<Col span={24}>
											<Divider />
										</Col>
									</>
								)}

								{/* Two Factor Auth */}
								<Col xs={24} md={12}>
									<Form.Item
										name="twoFactorAuth"
										label="Two-Factor Authentication"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Enabled" 
											unCheckedChildren="Disabled"
										/>
									</Form.Item>
								</Col>

								{/* Profile Private */}
								<Col xs={24} md={12}>
									<Form.Item
										name="profilePrivate"
										label="Private Profile"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Private" 
											unCheckedChildren="Public"
										/>
									</Form.Item>
								</Col>

								{/* Allow Messages */}
								<Col xs={24} md={12}>
									<Form.Item
										name="allowMessages"
										label="Allow Direct Messages"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Allowed" 
											unCheckedChildren="Blocked"
										/>
									</Form.Item>
								</Col>

								{/* Show Online Status */}
								<Col xs={24} md={12}>
									<Form.Item
										name="showOnlineStatus"
										label="Show Online Status"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Visible" 
											unCheckedChildren="Hidden"
										/>
									</Form.Item>
								</Col>
							</Row>
						</TabPane>

						{/* Notifications Tab */}
						<TabPane tab="Notifications" key="4">
							<Row gutter={[24, 16]}>
								{/* Email Notifications */}
								<Col span={24}>
									<Form.Item
										name="emailNotifications"
										label="Email Notifications"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Enabled" 
											unCheckedChildren="Disabled"
										/>
									</Form.Item>
									<p className="form-item-description">
										Receive notifications via email for important updates
									</p>
								</Col>

								{/* Push Notifications */}
								<Col span={24}>
									<Form.Item
										name="pushNotifications"
										label="Push Notifications"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Enabled" 
											unCheckedChildren="Disabled"
										/>
									</Form.Item>
									<p className="form-item-description">
										Receive push notifications in browser and mobile app
									</p>
								</Col>

								{/* Marketing Emails */}
								<Col span={24}>
									<Form.Item
										name="marketingEmails"
										label="Marketing Emails"
										valuePropName="checked"
									>
										<Switch 
											checkedChildren="Enabled" 
											unCheckedChildren="Disabled"
										/>
									</Form.Item>
									<p className="form-item-description">
										Receive promotional emails and newsletters
									</p>
								</Col>
							</Row>
						</TabPane>

					</Tabs>
				</Form>
			</Modal>
		);
	}
}

export default UserModal;
